import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Plus, RefreshCw, Package } from "lucide-react";
import { WorkspaceDialog } from "@/features/Workspaces/components/WorkspaceDialog";
import { WorkspacesTable } from "@/features/Workspaces/components/WorkspacesTable";
import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";
import { useQuery } from "@tanstack/react-query";
import { WorkspaceService, Workspace } from "@/features/Workspaces/services/workspaceService";
import { logger } from "@/infrastructure/lib/logger";
import { useSuperAdminOrg } from "@/shared/contexts/SuperAdminOrgContext";

// Interface para a UI do WorkspacesTable
interface WorkspaceTableItem {
  id: string;
  name: string;
  description: string | null;
  logo_url: string | null;
  product_users: { count: number }[];
}

export const WorkspaceSettings = () => {
  const { t } = useTranslation();
  const { selectedOrganization } = useSuperAdminOrg();
  // Segmento da queryKey: sem o id da organização ativa, a lista de workspaces
  // da organização anterior continuaria em cache após a troca de organização.
  const orgKeySegment = selectedOrganization?.id;
  const [dialogOpen, setDialogOpen] = useState(false);

  // Função para lidar com a abertura/fechamento do diálogo de produto
  const handleDialogChange = (open: boolean) => {
    setDialogOpen(open);
    // Invalidação é feita automaticamente pelos hooks useCreateProduct/useUpdateProduct
  };

  // Buscar produtos da organização do usuário atual usando JWT
  const {
    data: productsData,
    isLoading,
    error,
    refetch
  } = useQuery<Workspace[]>({
    queryKey: ['current-organization-products', orgKeySegment],
    queryFn: async () => {
      try {
        logger.debug('Loading current organization products');

        // Buscar produtos usando o token JWT
        const products = await WorkspaceService.getCurrentOrganizationProducts();
        logger.debug('Loaded current organization products', { count: products?.length || 0 });
        return products;
      } catch (error) {
                   // eslint-disable-next-line @typescript-eslint/no-explicit-any
        logger.error('Failed to load current organization products', { error: (error as any)?.message });
        throw error;
      }
    },
    retry: 2,
    retryDelay: 1000
  });

  // Adaptar os dados do produto para o formato esperado pelo WorkspacesTable
  const products: WorkspaceTableItem[] = productsData?.map((workspace: Workspace) => {
    return {
      id: workspace.id,
      name: workspace.name,
      description: workspace.description || null,
      logo_url: workspace.logo_url || null,
      product_users: [{ count: workspace.product_users_count || 0 }]
    };
  }) || [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div className="space-y-1.5">
          <CardTitle>{t("settings.workspaces.title")}</CardTitle>
          <CardDescription>{t("settings.workspaces.description")}</CardDescription>
        </div>
        <Button onClick={() => handleDialogChange(true)} data-tour="create-workspace-button">
          <Plus className="mr-2 h-4 w-4" />
          {t("settings.workspaces.addWorkspace")}
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <LoadingSpinner size="md" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center p-8 space-y-4">
            <p className="text-sm text-muted-foreground text-center mb-4">
              {t("common.errorLoadingData")}
            </p>
            <Button variant="outline" size="sm" onClick={() => refetch()} className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              {t("common.tryAgain")}
            </Button>
          </div>
        ) : products && products.length > 0 ? (
          <WorkspacesTable products={products} />
        ) : (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="rounded-full bg-primary/10 p-6 mb-4">
              <Package className="h-12 w-12 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">
              {t("settings.workspaces.noWorkspaces")}
            </h3>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              {t("settings.workspaces.noWorkspacesDescription")}
            </p>
          </div>
        )}
      </CardContent>
      <WorkspaceDialog
        open={dialogOpen}
        onOpenChange={handleDialogChange}
      />
    </Card>
  );
};
